
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

/**
 * ModuleLayout
 * A standardized layout for all HRMS/CRM modules.
 * Includes a unified header, breadcrumbs (optional), tabs, and a content area.
 * 
 * @param {string} title - The main title of the module (e.g., "Recruitment")
 * @param {React.ReactNode} action - Primary action button (e.g., "Create Job")
 * @param {Array<{id: string, label: string, count?: number}>} tabs - List of tabs
 * @param {string} activeTab - ID of the currently active tab
 * @param {Function} onTabChange - Callback when a tab is clicked
 * @param {React.ReactNode} children - The content of the active tab
 * @param {boolean} showBack - Whether to show the back button (defaults to false)
 * @param {Function} onBack - Custom back handler. If not provided, uses navigate(-1)
 */
const ModuleLayout = ({
    title,
    action,
    tabs = [],
    activeTab,
    onTabChange,
    children,
    showBack = false,
    onBack
}) => {
    const navigate = useNavigate();

    const handleBack = () => {
        if (onBack) {
            onBack();
        } else {
            navigate(-1);
        }
    };

    return (
        <div className="flex flex-col h-full bg-gray-50">
            {/* Module Header */}
            <div className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-4">
                        {showBack && (
                            <button
                                onClick={handleBack}
                                className="p-2 -ml-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
                                aria-label="Go back"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                        )}
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
                        </div>
                    </div>
                    <div className="flex items-center space-x-3">
                        {action}
                    </div>
                </div>

                {/* Tabs */}
                {tabs.length > 0 && (
                    <div className="flex space-x-6 -mb-4 overflow-x-auto">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => onTabChange(tab.id)}
                                className={`
                  pb-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors
                  ${activeTab === tab.id
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                `}
                            >
                                {tab.label}
                                {tab.count !== undefined && (
                                    <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${activeTab === tab.id ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                                        }`}>
                                        {tab.count}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-6 scroll-smooth">
                {children}
            </div>
        </div>
    );
};

export default ModuleLayout;
